import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createPlacement, updatePlacement } from '../placementActions';
import { fetchAdList } from '../adActions';
import { APP_ROUTES, AD_SIZES } from '../../../shared/constants';
import { extractContractError, formatErrorForDisplay, useIdempotencyKey } from '@bafkal/ui-shared/utils';
import { FormField, TextInput, Select } from '@bafkal/ui-shared/forms';
import { Card, ErrorMessage, Button } from '@bafkal/ui-shared/components';
import { colors } from '@bafkal/ui-shared/tokens';

// Transform targeting rules map → array of { key, value } rows for editing
const mapToRows = (rulesMap) => {
  if (!rulesMap || typeof rulesMap !== 'object') return [];
  return Object.entries(rulesMap).map(([key, value]) => ({
    key,
    value: Array.isArray(value) ? value.join(', ') : String(value),
  }));
};

// Transform array of { key, value } rows → targeting rules map
const rowsToMap = (rows) => {
  const map = {};
  rows.forEach(({ key, value }) => {
    if (!key.trim()) return;
    const trimmed = value.trim();
    if (trimmed.includes(',')) {
      map[key.trim()] = trimmed.split(',').map((v) => v.trim()).filter(Boolean);
    } else {
      map[key.trim()] = trimmed;
    }
  });
  return map;
};

const PlacementForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const idempotencyKey = useIdempotencyKey();

  const ads = useSelector((state) => state.ads.ads);
  const placements = useSelector((state) => state.placements.placements);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    adUnitId: '',
    targetUrl: '',
  });
  const [targetingRows, setTargetingRows] = useState([]);
  const isMounted = useRef(true);

  useEffect(() => {
    if (ads.length === 0) {
      dispatch(fetchAdList()).catch(() => {});
    }
  }, [dispatch, ads.length]);

  useEffect(() => {
    if (isEdit) {
      const existing = placements.find((p) => p.id === id);
      if (existing) {
        setFormData({
          adUnitId: existing.adUnitId || '',
          targetUrl: existing.targetUrl || '',
        });
        setTargetingRows(mapToRows(existing.targetingRules));
      }
      // If not found in state, the list hasn't been loaded — user navigated directly.
      // The form will be empty; they can cancel back to the list.
    }
  }, [id, isEdit, placements]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Set default ad unit once ads load (create mode only)
  useEffect(() => {
    if (!isEdit && ads.length > 0 && !formData.adUnitId) {
      setFormData((prev) => ({ ...prev, adUnitId: ads[0].id }));
    }
  }, [isEdit, ads, formData.adUnitId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRowChange = (index, field, value) => {
    setTargetingRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => {
    setTargetingRows((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeRow = (index) => {
    setTargetingRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isMounted.current) {
      setLoading(true);
      setError(null);
    }

    const rules = rowsToMap(targetingRows);
    const payload = {
      ...formData,
      ...(Object.keys(rules).length > 0 && { targetingRules: rules }),
    };

    try {
      if (isEdit) {
        await dispatch(updatePlacement(id, payload));
      } else {
        await dispatch(createPlacement(payload, idempotencyKey));
      }
      if (isMounted.current) history.push(APP_ROUTES.INVENTORY.PLACEMENTS);
    } catch (err) {
      const contractError = extractContractError(err, 'Failed to save placement');
      if (isMounted.current) setError(formatErrorForDisplay(contractError));
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  return (
    <div className="py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <h1 className="text-2xl font-semibold text-gray-900 mb-8">
            {isEdit ? 'Edit Placement' : 'Create New Placement'}
          </h1>

          <ErrorMessage message={error} />

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Ad Unit */}
            <FormField label="Ad Unit">
              <Select
                id="adUnitId"
                name="adUnitId"
                required
                value={formData.adUnitId}
                onChange={handleChange}
              >
                {ads.length === 0 && (
                  <option value="" disabled>Loading ad units…</option>
                )}
                {ads.map((ad) => {
                  const sizeInfo = AD_SIZES[ad.size];
                  const sizeLabel = sizeInfo
                    ? sizeInfo.dimensions
                      ? ` (${sizeInfo.label} ${sizeInfo.dimensions.width}x${sizeInfo.dimensions.height})`
                      : ` (${sizeInfo.label})`
                    : ad.size
                    ? ` (${ad.size})`
                    : '';
                  return (
                    <option key={ad.id} value={ad.id}>
                      {ad.name}{sizeLabel}
                    </option>
                  );
                })}
              </Select>
            </FormField>

            {/* Target URL */}
            <FormField label="Target URL">
              <TextInput
                type="text"
                id="targetUrl"
                name="targetUrl"
                required
                value={formData.targetUrl}
                onChange={handleChange}
                placeholder="https://example.com/page"
              />
            </FormField>

            {/* Targeting Rules */}
            <FormField
              label="Targeting Rules"
              helperText="Separate multiple values with commas (e.g. US, CA)"
            >
              {targetingRows.length === 0 && (
                <p style={{ fontSize: '0.875rem', color: colors.neutral[400], fontStyle: 'italic', marginBottom: '8px' }}>
                  No targeting rules. Click "Add Rule" to add one.
                </p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                {targetingRows.map((row, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ width: '33%' }}>
                      <TextInput
                        value={row.key}
                        onChange={(e) => handleRowChange(index, 'key', e.target.value)}
                        placeholder="Key (e.g. country)"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        value={row.value}
                        onChange={(e) => handleRowChange(index, 'value', e.target.value)}
                        placeholder="Value (e.g. US, CA)"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => removeRow(index)}
                      style={{ color: colors.neutral[400], fontSize: '1.25rem', lineHeight: 1, padding: '0 4px', flexShrink: 0, textDecoration: 'none' }}
                      aria-label="Remove rule"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={addRow}>
                + Add Rule
              </Button>
            </FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px' }}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => history.push(APP_ROUTES.INVENTORY.PLACEMENTS)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving…' : 'Save Placement'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PlacementForm;
